import { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet";
import { Box, IconButton, Stack, Typography } from "@mui/material";
import { ArrowBackRounded } from "@mui/icons-material";
import AutoResizeText from "../../components/AutoResizeText";
import { useAppState } from "../../state";
import Switch from "../../components/Switch";
import { colors } from "../../theme";
import { Loader } from "../../components/Loader";
import useArtQuiz from "./useArtQuiz";
import { quizToTagMapping } from "./quizMapping";
import Button from "../../components/Button";
import CategoryCard from "./CategoryCard";
import ArtQuizResultGrid from "./ArtQuizResultGrid";

const ArtQuiz = () => {
  const { isMobileScreen, navHeight } = useAppState();
  const [step, setStep] = useState(1);
  const [selectedGrouping, setSelectedGrouping] = useState<"single" | "doubles" | "gallery-walls">("single");
  const {
    getProducts,
    selectedSubjects,
    setSelectedSubjects,
    selectedStyles,
    setSelectedStyles,
    selectedMoods,
    setSelectedMoods,
    singles,
    doubles,
    galleryWalls,
    loadMore,
    hasMore,
    loading,
  } = useArtQuiz();

  const onClickSubject = (subject: string) => {
    if (selectedSubjects.includes(subject)) {
      setSelectedSubjects(selectedSubjects.filter((s) => s !== subject));
    } else {
      setSelectedSubjects([...selectedSubjects, subject]);
    }
  };

  const onClickStyles = (style: string) => {
    if (selectedStyles.includes(style)) {
      setSelectedStyles(selectedStyles.filter((s) => s !== style));
    } else {
      setSelectedStyles([...selectedStyles, style]);
    }
  };

  const onClickMood = (mood: string) => {
    if (selectedMoods.includes(mood)) {
      setSelectedMoods(selectedMoods.filter((m) => m !== mood));
    } else {
      setSelectedMoods([...selectedMoods, mood]);
    }
  };

  const backButtonDisabled = step === 1;
  const nextButtonDisabled =
    (step === 1 && selectedSubjects.length === 0) ||
    (step === 2 && selectedStyles.length === 0) ||
    (step === 3 && selectedMoods.length === 0);

  useEffect(() => {
    if (step === 4) getProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  const getStepOptions = (step: number) => {
    switch (step) {
      case 1:
        return Object.keys(quizToTagMapping.subject).map((label) => ({
          label,
          value: label,
          tags: quizToTagMapping.subject[label as keyof typeof quizToTagMapping.subject],
        }));
      case 2:
        return Object.keys(quizToTagMapping.style).map((label) => ({
          label,
          value: label,
          tags: quizToTagMapping.style[label as keyof typeof quizToTagMapping.style],
        }));
      case 3:
        return Object.keys(quizToTagMapping.mood).map((label) => ({
          label,
          value: label,
          tags: quizToTagMapping.mood[label as keyof typeof quizToTagMapping.mood],
        }));
      default:
        return [];
    }
  };

  const getStepTitle = (step: number) => {
    switch (step) {
      case 1:
        return "What catches your eye most?";
      case 2:
        return "What style speaks to you?";
      case 3:
        return "What mood are you looking to create?";
      default:
        return "";
    }
  };

  const selectedCategories = useMemo(() => {
    return [...selectedSubjects, ...selectedStyles, ...selectedMoods];
  }, [selectedSubjects, selectedStyles, selectedMoods]);

  const onClick = (category: string) => {
    if (step === 1) return onClickSubject(category);
    if (step === 2) return onClickStyles(category);
    if (step === 3) return onClickMood(category);
  };

  return (
    <>
      <Helmet>
        <title>Art Quiz | GoodMood</title>
        <meta
          name="description"
          content="Take our art quiz to find the perfect print set for your space. We'll ask you a few questions to help you find the perfect pieces."
        />
      </Helmet>

      {step !== 4 && (
        <Stack
          gap={2}
          padding={{ xs: 2, md: 5 }}
          position="fixed"
          top={navHeight}
          left={0}
          width="100%"
          height={`calc(100vh - ${navHeight}px)`}
          zIndex={1}
          bgcolor={colors.white}
          overflow="auto"
          alignItems="center"
        >
          <Stack direction="row" gap={1} alignItems="center" paddingX={2} paddingY={1} bgcolor={colors.grey02} borderRadius={16}>
            <Typography fontSize={14} component="span">
              Step
            </Typography>
            <Stack
              bgcolor={step === 1 ? colors.black : colors.white}
              width={25}
              height={25}
              borderRadius={25}
              alignItems="center"
              justifyContent="center"
              onClick={() => setStep(1)}
              sx={{ cursor: "pointer" }}
            >
              <Typography fontSize={14} component="span" color={step === 1 ? colors.white : colors.black}>
                1
              </Typography>
            </Stack>
            <Stack
              bgcolor={step === 2 ? colors.black : colors.white}
              width={25}
              height={25}
              borderRadius={25}
              alignItems="center"
              justifyContent="center"
              onClick={() => selectedSubjects.length > 0 && setStep(2)}
              sx={{ cursor: selectedSubjects.length > 0 ? "pointer" : "default" }}
            >
              <Typography fontSize={14} component="span" color={step === 2 ? colors.white : colors.black}>
                2
              </Typography>
            </Stack>
            <Stack
              bgcolor={step === 3 ? colors.black : colors.white}
              width={25}
              height={25}
              borderRadius={25}
              alignItems="center"
              justifyContent="center"
              onClick={() => selectedStyles.length > 0 && setStep(3)}
              sx={{ cursor: selectedStyles.length > 0 ? "pointer" : "default" }}
            >
              <Typography fontSize={14} component="span" color={step === 3 ? colors.white : colors.black}>
                3
              </Typography>
            </Stack>
          </Stack>

          <Stack direction="row" justifyContent="center" width="auto" paddingBottom={24} marginX="auto">
            {step >= 1 && step <= 3 && (
              <Stack gap={4} width="100%">
                <Typography variant="h2" align="center">
                  {getStepTitle(step)}
                </Typography>
                <Stack gap={{ xs: 2, md: 2 }} alignItems="center">
                  {getStepOptions(step).map((option) => (
                    <CategoryCard
                      key={option.value}
                      category={option}
                      selectedCategories={selectedCategories}
                      onClick={onClick}
                    />
                  ))}
                </Stack>
                {!isMobileScreen && (
                  <Box>
                    <Stack direction="row" gap={1.5}>
                      <IconButton
                        onClick={() => setStep(Math.max(1, step - 1))}
                        disabled={backButtonDisabled}
                        color="secondary"
                        sx={{ backgroundColor: colors.black }}
                      >
                        <ArrowBackRounded fontSize="small" />
                      </IconButton>
                      <Button fullWidth onClick={() => setStep(Math.min(5, step + 1))} disabled={nextButtonDisabled}>
                        {step === 3 ? "Find art" : "Next"}
                      </Button>
                    </Stack>
                  </Box>
                )}
              </Stack>
            )}
          </Stack>

          {isMobileScreen && (
            <Box position="fixed" width="100vw" left={0} bottom={0} bgcolor={colors.white} paddingX={2} paddingY={3}>
              <Stack direction="row" gap={1.5}>
                <IconButton
                  onClick={() => setStep(Math.max(1, step - 1))}
                  disabled={backButtonDisabled}
                  color="secondary"
                  sx={{ backgroundColor: colors.black }}
                >
                  <ArrowBackRounded fontSize="small" />
                </IconButton>
                <Button fullWidth onClick={() => setStep(Math.min(5, step + 1))} disabled={nextButtonDisabled}>
                  Next
                </Button>
              </Stack>
            </Box>
          )}
        </Stack>
      )}

      {step === 4 && (
        <Stack gap={{ xs: 1 }} paddingTop={2} paddingBottom={{ xs: 3, md: 5 }} position="relative">
          <AutoResizeText text="Art Quiz" align="center" maxSize={isMobileScreen ? 50 : 85} />

          <Stack
            position="sticky"
            top={`${navHeight}px`}
            zIndex={1}
            bgcolor={colors.white}
            direction={{ xs: "column-reverse", md: "row" }}
            alignItems="center"
            justifyContent="space-between"
            paddingX={{ xs: 2, md: 5 }}
            paddingY={{ xs: 1, md: 2 }}
            gap={2}
          >
            <Stack width="139px"></Stack>
            <Switch
              options={[
                { label: "Single", value: "single" },
                { label: "Doubles", value: "doubles" },
                { label: "Gallery Walls", value: "gallery-walls" },
              ]}
              fullWidth={isMobileScreen}
              onChange={(value) => setSelectedGrouping(value as "single" | "doubles" | "gallery-walls")}
              selected={selectedGrouping}
              minWidth={315}
            />

            <Button size="small" secondary onClick={() => setStep(1)}>
              Update answers
            </Button>
          </Stack>

          {loading ? (
            <Loader />
          ) : (
            <ArtQuizResultGrid
              printGroups={selectedGrouping === "single" ? singles : selectedGrouping === "doubles" ? doubles : galleryWalls}
              loading={loading}
              loadMore={loadMore}
              hasMore={hasMore}
              setStep={setStep}
              key={selectedGrouping}
            />
          )}
        </Stack>
      )}
    </>
  );
};

export default ArtQuiz;
